import Vue from 'vue';
import Vuex from 'vuex';
import VuexPersist from 'vuex-persist';
import isEqual from 'lodash.isequal'
import signalR from '@/libs/signalR/vuexStore/store';
import { ExerciseDocumentType } from '@/libs/types';
import ACTIONS from './storeActions';
import BusinessLogicRunInfo from './libs/businessLogicRunInfo';

Vue.use(Vuex);

const vuexLocalStorage = new VuexPersist({
  key: 'vuex', // The key to store the state on in the storage provider.
  storage: window.localStorage, // or window.sessionStorage or localForage
  // Function that passes the state and returns the state with only the objects you want to store.
  // reducer: state => state,
  // Function that passes a mutation and lets you decide if it should update the state in localStorage.
  // filter: mutation => (true)
});

const getDefaultState = () => ({
  bearerToken: null,
  runInfo: {},
  runDocuments: [],
  user: null,
  observerTeamId: null,
  observerTimeTable: [],
  currentObserverId: null,
  currentExerciseId: null,
  currentInterviewId: undefined,
  currentExerciseDocuments: [],
  currentExerciseDocumentsLoadingState: 0,
  currentParticipantId: null,
  snackbar: {
    text: null,
    color: null,
  },
  isLoading: false,
  language: navigator.language.substring(0, 2),
  assessments: null,
  tmpAssessments: [],
  participantImages: {},
  observations: [],
  interviews: [],
});

const getters = {
  syncFromAppToCockpitAllowed(state) {
    return !state.runInfo?.observerTraining
  },
  runId(state) {
    return () => state.runInfo.id
  },
  runDocuments(state) {
    return () => state.runDocuments;
  },
  bearerToken(state) {
    return () => state.bearerToken;
  },
  snackbar(state) {
    return () => state.snackbar;
  },
  language(state) {
    return () => state.language;
  },
  isLoading(state) {
    return () => state.isLoading;
  },
  assessments(state) {
    return () => state.assessments;
  },
  tmpAssessments(state) {
    return () => state.tmpAssessments;
  },
  participantImages(state) {
    return () => state.participantImages;
  },
  currentObserver(state) {
    if (!state.runInfo || !state.runInfo.teams) {
      return null;
    }

    for (const team of state.runInfo.teams) {
      if (!team || !team.members) {
        return null;
      }

      for (const observer of team.members) {
        if (observer.id === state.user.userId) {
          return observer;
        }
      }
    }

    return null;
  },
  isGuestObserver(state) {
    // if no runInfo or no Teams are available - handle it as if the user accessing it is a guest observer - thus cannot change anything
    if (!state.runInfo || !state.runInfo.teams) return true
    for (const team of state.runInfo.teams) {
      if (!team || !team.members) return true
      for (const observer of team.members) {
        if (observer.id === state.user.userId) return !observer.isObserving
      }
    }
    return true;
  },
  currentExercise(state) {
    if (!state.runInfo || !state.runInfo.exercises || !state.currentExerciseId) {
      return null;
    }

    for (const exercise of state.runInfo.exercises) {
      if (!exercise) {
        return null;
      }
      if (exercise.id === state.currentExerciseId) {
        return exercise;
      }
    }

    return null;
  },
  currentExerciseDocuments(state) {
    if (!state.runInfo || !state.runInfo.exercises || !state.currentExerciseId) return null;
    const exercise = state.runInfo.exercises.find((ex) => ex.id === state.currentExerciseId);
    const docs = exercise.documents.filter((document) => document.type === ExerciseDocumentType.Observer)
    return docs
  },
  currentParticipant(state) {
    if (!state.runInfo || !state.runInfo.participants || !state.currentParticipantId) {
      return null;
    }

    for (const participant of state.runInfo.participants) {
      if (!participant) {
        return null;
      }
      if (participant.id === state.currentParticipantId) {
        return participant;
      }
    }

    return null;
  },
  currentInterview(state) {
    if (!state.runInfo || !state.interviews || !state.currentInterviewId) return null;
    for (const interview of state.interviews) {
      if (!interview) return null;
      if (interview.id === state.currentInterviewId) return interview
    }
    return null;
  },
}

const mutations = {
  setBearerToken(state, bearerToken) {
    state.bearerToken = bearerToken;
    if (bearerToken) {
      // console.log('TOKEN WAS SET');‚
      Vue.http.headers.common.Authorization = `Bearer ${bearerToken}`;
    } else {
      state.currentUser = null;
      delete Vue.http.headers.common.Authorization;
    }
  },
  setRunInfo(state, data) {
    state.runInfo = {
      ...data
    }
    const { runInfo } = state;
    const { user } = state;
    const businessLogicRunInfo = new BusinessLogicRunInfo(this, runInfo, user);

    state.observerTeamId = businessLogicRunInfo.findObserverTeamId();
    state.observerTimeTable = businessLogicRunInfo.createObserverTimeTable(state.observerTeamId);
  },
  setRunDocuments(state, data) {
    state.runDocuments = data;
  },
  setUser(state, data) {
    state.user = data;
  },
  setCurrentExerciseAndParticipant(state, data) {
    state.currentParticipantId = data.participantId;
    state.currentExerciseId = data.exerciseId;
    state.currentExerciseDocumentsLoadingState = 0;
  },
  setCurrentParticipant(state, data) {
    state.currentParticipantId = data.participantId;
  },
  setCurrentInterview(state, data) {
    state.currentInterviewId = data.interviewId;
  },
  removeCurrentInterview(state, data) {
    state.currentInterviewId = undefined;
  },
  setCurrentExerciseDocuments(state, data) {
    state.currentExerciseDocuments = data;
  },
  setExerciseDocumentsLoadingState(state, data) {
    state.currentExerciseDocumentsLoadingState = data;
  },
  setSnackbar(state, payload) {
    state.snackbar.color = payload.color;
    state.snackbar.text = payload.text;
  },
  setIsLoading(state, payload) {
    state.isLoading = payload;
  },
  setParticipantImage(state, payload) {
    state.participantImages[payload.id] = payload.data;
  },
  setAssessments(state, payload) {
    state.assessments = payload;
  },
  setTmpAssessments(state, payload) {
    state.tmpAssessments = payload;
  },
  updateRunState(state, update) {
    const oldState = state.runInfo.runInformation.runState || 0;
    state.runInfo.runInformation.runState = update.runState;
    state.runInfo.runInformation.syncFromAppToCockpitAllowed = update.syncFromAppToCockpitAllowed;

    if (oldState === 2 && update.runState === 3) {
      console.log('Remove temporary training data');
      state.assessments = state.assessments.filter( (a) => a.id );
      state.observations = state.observations.filter( (o) => o.id );
    }
  },
  /**
   * @param state
   * @param {Object} payload - contains the current and the saved assessments
   * @param {Array<Object>} payload.current - Array of the currently saving assessments
   * @param {Array<Object>} payload.response - Array of responded assessments after save
   * @desc handles the store update for all assessments, if new will use the temporaryId to find the assessment in the store that was saved before it was send to the server
   */
  updateAssessments(state, payload) {
    const { current, response } = payload;
    // map current and created assessments back to each other
    response.forEach( (ass) => {
      const tmp = current.find((c) => c.participantId === ass.participantId && c.matrixId === ass.matrixId);
      let stateIndex = -1;

      if (tmp.temporaryId) stateIndex = state.assessments.findIndex( (a) => a?.temporaryId === tmp.temporaryId);
      else stateIndex = state.assessments.findIndex( (a) => (a.participantId === ass.participantId && a.matrixId === ass.matrixId))
      if (stateIndex <= -1) state.assessments.push(ass)
      else state.assessments = [...state.assessments.slice(0, stateIndex), ass, ...state.assessments.slice( stateIndex + 1)]
    })
  },
  setSingleAssessment(state, payload) {
    // todo fix setSingleAssessment
    if (payload && state.assessments && Array.isArray(state.assessments)) {
      const index = state.assessments.findIndex((a) => (a.id && a.id === payload.id) || (a.temporaryId && a.temporaryId === payload.temporaryId));
      // if (index && index >= 0) defaults always to falls if the index is 0...
      if (index <= -1) {
        state.assessments.push(payload)
      } else {
        // actually trigger an update of the state
        state.assessments = [...state.assessments.slice(0, index), payload, ...state.assessments.slice(index + 1)]
      }
    }
  },
  setSingleTmpAssessment(state, payload) {
    if (payload && state.tmpAssessments && Array.isArray(state.tmpAssessments)) {
      const index = state.tmpAssessments.findIndex((a) => (a.id && a.id === payload.id))
      if (index <= -1) state.tmpAssessments.push(payload);
      else state.tmpAssessments = [...state.tmpAssessments.slice(0, index), payload, ...state.tmpAssessments.slice(index + 1)]
    }
  },
  updateTmpAssessment(state, payload) {
    const findAndApply = (ass) => {
      const index = state.tmpAssessments.findIndex((x) => x.id && x.id === ass.id);
      if (index > -1) {
        const toModify = state.tmpAssessments.find((x) => (x.id && x.id === ass.id))
        if (toModify) {
          for (const key of Object.keys(ass)) {
            toModify[key] = ass[key];
          }
          console.log('Updated TmpAssessment in Store', toModify)
        }
      } else {
        state.tmpAssessments.push(ass)
      }
    }
    if (Array.isArray(payload)) {
      for (const item of payload) findAndApply(item)
    } else {
      findAndApply(payload)
    }
  },
  pushInterviews(state, payload) {
    if (!state.interviews) {
      state.interviews = [];
    }
    if (Array.isArray(payload)) {
      for (const interview of payload) {
        const tmp = state.interviews.find((x) => x.id === interview.id);
        if (tmp && isEqual(tmp, interview)) return
        state.interviews.push(interview);
      }
    } else {
      const tmp = state.interviews.find((x) => x.id === payload.id);
      if (tmp && isEqual(tmp, payload)) return;
      state.interviews.push(payload)
    }
  },
  pushObservations(state, payload) {
    // init target dictionary
    if (!state.observations) {
      state.observations = [];
    }

    // todo remove colliding object

    // Unwrap array
    if (Array.isArray(payload)) {
      for (const obs of payload) {
        state.observations.push(obs);
      }
    } else {
      state.observations.push(payload);
    }

  },
  removeObservation(state, observation) {
    const toRemove = state.observations.filter( (obs) => (!obs.temporaryId && !obs.id) || (observation.id === obs.id) || (observation.temporaryId && observation.temporaryId === obs.temporaryId) );
    for (const removeItem of toRemove) {
      const index = state.observations.indexOf(removeItem);
      if (index > -1) {
        state.observations.splice(index, 1);
      }
    }
  },
  updateObservations(state, observations) {
    const findAndApply = (observation) => {
      const toModify = state.observations.find( (x) => (x.id && x.id === observation.id) || (x.temporaryId && x.temporaryId === observation.temporaryId));

      if (toModify) {
        // Apply all keys seperately, maybe because toModify is a store modified reactive object, that does not work with Object.assign.
        for (const key of Object.keys(observation)) {
          toModify[key] = observation[key];
        }
        console.log('UPDATED OBSERVATION IN STORE', toModify);
      }
    };

    if (Array.isArray(observations)) {
      for (const item of observations) {
        findAndApply(item);
      }
    } else {
      findAndApply(observations);
    }

  },
  clearState(state) {
    Object.assign(state, getDefaultState());
  },
  [ACTIONS.SIGNAL_R__HANDLE_OBSERVERTRAINING]: (state, observerTraining) => {
    state.runInfo = { ...state.runInfo, observerTraining }
  }
}

const actions = {
  setBearerToken(context, bearerToken) {
    context.commit(ACTIONS.SET_BEARER_TOKEN, bearerToken);
  },
  setRunInfo(context, data) {
    context.commit(ACTIONS.SET_RUN_INFO, data);
  },
  setRunDocuments(context, data) {
    context.commit(ACTIONS.SET_RUN_DOCUMENTS, data);
  },
  setSnackbar(context, payload) {
    context.commit(ACTIONS.SET_SNACKBAR, payload);
  },
  setCurrentExerciseAndParticipant(context, data) {
    context.commit(ACTIONS.SET_CURRENT_EXERCISE_AND_CANDIDATE, data);
  },
  setCurrentParticipant(context, data) {
    context.commit(ACTIONS.SET_CURRENT_CANDIDATE, data);
  },
  setCurrentInterview(context, data) {
    context.commit(ACTIONS.SET_CURRENT_INTERVIEW, data);
  },
  removeCurrentInterview(context, data) {
    context.commit(ACTIONS.REMOVE_CURRENT_INTERVIEW);
  },
  setCurrentExerciseDocuments(context, data) {
    context.commit(ACTIONS.SET_CURRENT_EXERCISE_DOCUMENTS, data);
  },
  setExerciseDocumentsLoadingState(context, data) {
    context.commit(ACTIONS.SET_EXERCISE_DOCUMENTS_LOADING_STATE, data);
  },
  setUser(context, data) {
    context.commit(ACTIONS.SET_USER, data);
  },
  setIsLoading(context, data) {
    context.commit(ACTIONS.SET_IS_LOADING, data);
  },
  setAssessments(context, data) {
    context.commit(ACTIONS.SET_ASSESSMENTS, data);
  },
  updateAssessments(context, data) {
    context.commit(ACTIONS.UPDATE_ASSESSMENTS, data)
  },
  setSingleAssessment(context, data) {
    context.commit(ACTIONS.SET_SINGLE_ASSESSMENT, data);
  },
  setSingleTmpAssessment(context, data) {
    context.commit(ACTIONS.SET_SINGLE_TMP_ASSESSMENT, data)
  },
  updateTmpAssessment(context, data) {
    context.commit(ACTIONS.UPDATE_TMP_ASSESSMENT, data)
  },
  setParticipantImage(context, data) {
    context.commit(ACTIONS.SET_CANDIDATE_IMAGE, data);
  },
  pushInterviews(context, data) {
    context.commit(ACTIONS.PUSH_INTERVIEWS, data);
  },
  pushObservations(context, data) {
    context.commit(ACTIONS.PUSH_OBSERVATIONS, data);
  },
  removeObservation(context, observation) {
    context.commit(ACTIONS.REMOVE_OBSERVATION, observation);
  },
  updateObservations(context, observation) {
    context.commit(ACTIONS.UPDATE_OBSERVATIONS, observation);
  },
  updateRunState(context, runState) {
    context.commit(ACTIONS.UPDATE_RUNSTATE, runState);
  },
  clearState(context) {
    context.commit(ACTIONS.CLEAR_STATE);
  },
  [ACTIONS.SIGNAL_R__HANDLE_OBSERVERTRAINING]: (context, payload) => context.commit(ACTIONS.SIGNAL_R__HANDLE_OBSERVERTRAINING, payload)
}

export default new Vuex.Store({
  plugins: [vuexLocalStorage.plugin],
  modules: {
    signalR
  },
  state: getDefaultState(),
  getters,
  mutations,
  actions,
});
